<script setup lang="tsx">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { History, Response } from '@/models/api-generated'
import http from '@/services/http'
import { formatDate, genRouteID } from '@/services/utils'

const props = defineProps<{ assetId: number }>()
const assetHistory = ref<History[]>([])
const { t, locale, te } = useI18n()

const fetchHistory = async () => {
	if (!props.assetId) return
	const { data } = await http.get<Response<History[]>>(`api/asset/history/${genRouteID(props.assetId, '', 'asset')}`)
	assetHistory.value = data.data || []
}

watch(() => props.assetId, fetchHistory)
onMounted(fetchHistory)

const getEntityName = (structName: string | number): string => {
	const name = String(structName)
	switch (name) {
		case 'BusinessProcess':
			return t('businessProcess')
		case 'StandardModuleAsset':
			return t('module.label')
		default:
			return name
	}
}

const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T|\s)?\d{0,2}:?\d{0,2}:?\d{0,2}?/

const renderValue = (val: unknown): string => {
	if (val === null || val === undefined || val === '' || val === '{}' || val === '<nil>') return t('misc.nothing')
	const str = String(val)
	if (isoDateRegex.test(str)) {
		const date = new Date(str)
		if (!isNaN(date.getTime())) return formatDate(date)
	}
	return str
}

const getJoinedMap = (map: Record<number | string, string>): string => Object.values(map).join(', ')

const convertTimestamp = (timestamp: string): string => {
	const ts = parseInt(timestamp, 10)
	if (isNaN(ts)) return ''
	const date = new Date(ts * 1000)
	return date.toLocaleString(locale.value, {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})
}

const translateDescription = (desc: string): string => {
	if (!desc.includes(':')) return translateSafe(`asset.history.${desc}`, desc)
	const [prefix, ...rest] = desc.split(':')
	const key = rest.join(':').trim()
	return `${prefix}: ${translateSafe(`asset.history.${key}`, key)}`
}

const translateSafe = (key: string, fallback: string) => {
	return te(key) ? t(key) : fallback
}

const getFieldLabel = (field: string): string => t(`asset.titles.${field}`, field)
</script>

<template>
	<div class="mt-4 ms-2">
		<template v-if="assetHistory.length">
			<div v-for="(item, key) in assetHistory" :key="key" class="history-item ps-3">
				<p class="mb-0 ps-4">
					<span class="history-item-title">
						{{ translateDescription(item.description) }}
					</span>
				</p>

				<template v-if="item.updatedValues?.length">
					<div class="d-flex ps-4 mt-2 mb-2">
						<div class="d-flex flex-column w-100">
							<div v-for="(field, idx) in item.fieldNames" :key="idx" class="d-flex mb-1 align-items-start">
								<div class="fw-bold me-2">{{ getFieldLabel(field) }}:</div>
								<div class="flex-grow-1">
									{{ renderValue(item.originalValues?.[idx]) }} <i class="bi bi-arrow-right"></i> {{ renderValue(item.updatedValues[idx]) }}
								</div>
							</div>
						</div>
					</div>
				</template>

				<template v-if="item.addedConnections || item.removedConnections">
					<template v-if="item.addedConnections">
						<p class="mb-0 ps-4 mt-2">
							<strong>{{ t('asset.history.addedConnections') }}</strong>
						</p>
						<div class="d-flex ps-4 mb-2">
							<div class="d-flex flex-column">
								<p v-for="(connMap, entityName) in item.addedConnections" :key="entityName" class="mb-0 mt-2">
									{{ getEntityName(entityName) }}:
								</p>
							</div>
							<div class="d-flex flex-column ps-3">
								<p v-for="(connMap, entityName) in item.addedConnections" :key="entityName + '-value'" class="mb-0 mt-2">
									{{ getJoinedMap(connMap) }}
								</p>
							</div>
						</div>
					</template>

					<template v-if="item.removedConnections">
						<p class="mb-0 ps-4 mt-2">
							<strong>{{ t('asset.history.removedConnections') }}</strong>
						</p>
						<div class="d-flex ps-4 mb-2">
							<div class="d-flex flex-column">
								<p v-for="(connMap, entityName) in item.removedConnections" :key="entityName + '-removed'" class="mb-0 mt-2">
									{{ getEntityName(entityName) }}:
								</p>
							</div>
							<div class="d-flex flex-column ps-3">
								<p v-for="(connMap, entityName) in item.removedConnections" :key="entityName + '-removed-val'" class="mb-0 mt-2">
									{{ getJoinedMap(connMap) }}
								</p>
							</div>
						</div>
					</template>
				</template>

				<p class="ps-4 text-muted small">
					{{ convertTimestamp(item.date) }}
					<span class="mx-2">&#9679;</span>
					{{ item.responsible.firstname }} {{ item.responsible.lastname }}
				</p>
			</div>
		</template>
		<template v-else>
			<p>{{ t('asset.history.empty') }}</p>
		</template>
	</div>
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

.small-text
  font-size: $small-font-size
</style>
